import { TextField } from "@mui/material";
import React, { useEffect, useLayoutEffect, useRef, useState } from "react";
import "../../../../number.extensions";
import useCheckIfCanBeConvertedToNumber, {
  isStringConvertibleToNumber,
} from "../../../../utils/convert-to-number-utils";
import { thousandSeparatorType } from "../../../../utils/number-thousand-separator-utils";

interface Props {
  required?: boolean;
  disabled?: boolean;
  placeHolder?: string | undefined;
  field: string;
  labelKey: string;
  handleChange: (field: string, value: number | null) => void;
  amountValue: number;
  sxProps?: object;
  endAdornment?: JSX.Element | undefined;
  error?: boolean;
  helperText?: string;
}

export const TextFieldTypeAmount = ({
  required,
  disabled,
  placeHolder,
  field,
  labelKey,
  handleChange,
  amountValue,
  sxProps,
  endAdornment,
  error,
  helperText,
}: Props) => {
  const canBeConvertedToNumber = useCheckIfCanBeConvertedToNumber();

  const [firstRender, setFirstRender] = useState<boolean>(true);

  const [amount, setAmount] = useState<string>("");

  const [oldAmount, setOldAmount] = useState<string>("");

  const [cursorNewPosition, setCursorNewPosition] = useState<number>(0);

  const [formatAmountValue, setFormatAmountValue] = useState<boolean>(false);

  const [formattedValue, setFormattedValue] = useState<string>("");

  const inputRef = useRef<HTMLInputElement | null>(null);

  // extract decimal separator from user preferences

  const userLocale = thousandSeparatorType || navigator.language;

  const convertedToLocale = new Intl.NumberFormat(userLocale).format(1000.25);
  const thousandSeparator = isNaN(parseFloat(convertedToLocale[1])) ? convertedToLocale[1] : "\u2019"; // some browsers do not add thousand separator

  const decimalSeparator = convertedToLocale[convertedToLocale.length - 3];

  // #region handle change amount
  const handleChangeAmount = (value: string) => {
    if (value.split("-").length > 2 || !isStringConvertibleToNumber(value)) return;

    setOldAmount(amount);

    const integerPartOfValue = value.split(decimalSeparator)[0];
    const decimalPartOfValue = value
      .split(decimalSeparator)[1]
      ?.split("")
      ?.filter((char) => char !== thousandSeparator)
      .join("");

    const hasNotAllowedCharacters = Number.isNaN(
      Number(
        value
          .split("")
          .filter((char) => char !== decimalSeparator && char !== thousandSeparator && char !== "-")
          .join("")
      )
    );

    if (hasNotAllowedCharacters) {
      // To ignore comma, dot, space, single quote, minus and plus
      const regex = /[.,'\u2019 \-+'"]/g;

      const convertedToLocale = new Intl.NumberFormat("it-CH").format(1000.25);
      // Exctract apostrophe to remove it
      // const thousandsSeparator = convertedToLocale[1];
      const thousandsSeparator = isNaN(parseFloat(convertedToLocale[1])) ? convertedToLocale[1] : "\u2019";

      // find index of first dot or comma => it will be decimal separator
      const indexOfDecimalSeparator = value.split("").findIndex((char) => char === "." || char === ",");

      if (indexOfDecimalSeparator >= 0) {
        const integer = value.substring(0, indexOfDecimalSeparator);
        const decimalPart = value.substring(indexOfDecimalSeparator + 1, value.length);

        const resultInteger = integer.replace(regex, "").split(thousandsSeparator).join("");
        const resultDecimalPart = decimalPart.replace(regex, "").split(thousandsSeparator).join("");

        const finalAmount =
          value[0] === "-"
            ? `-${resultInteger}${decimalSeparator}${resultDecimalPart}`
            : `${resultInteger}${decimalSeparator}${resultDecimalPart}`;

        handleChange(field, finalAmount.toNumberFromLocaleCustom());
      } else {
        //Remove the specified characters from the string
        const resultString =
          value[0] === "-"
            ? `-${value.replace(regex, "").split(thousandsSeparator).join("")}`
            : value.replace(regex, "").split(thousandsSeparator).join("");

        handleChange(field, resultString.toNumberFromLocaleCustom());
      }
    } else if (canBeConvertedToNumber(value) === "cutFirstChar") {
      setAmount(value.slice(1));

      handleChange(field, null);
    } else if (value.includes(decimalSeparator)) {
      const integerPartOfValueWithoutMinus = integerPartOfValue.split("-").join("");

      if (!integerPartOfValueWithoutMinus || Number(integerPartOfValueWithoutMinus) === 0) {
        handleChange(field, value.toNumberFromLocaleCustom());

        setFormattedValue(`${integerPartOfValue}${decimalSeparator}${decimalPartOfValue}`);
      } else {
        handleChange(field, value.toNumberFromLocaleCustom());

        const newIntegerPart = value.toNumberFromLocaleCustom().toLocaleCustom().split(decimalSeparator)[0];
        setFormattedValue(`${newIntegerPart}${decimalSeparator}${decimalPartOfValue}`);
      }
    } else if (canBeConvertedToNumber(value)) {
      handleChange(field, value.toNumberFromLocaleCustom());
      value.toNumberFromLocaleCustom() === amountValue && setAmount(amountValue.toLocaleCustom("allDigits"));
    } else {
      setAmount(value);

      handleChange(field, null);
    }

    if (inputRef.current) {
      const cursorPosition = inputRef.current.selectionStart; // Get cursor position

      setCursorNewPosition(cursorPosition ?? 0);
    }
  };
  // #endregion

  // #region handle key down
  const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    // Allow numbers, decimal separator, and specific keyboard actions (like backspace and delete)
    const allowedCharactersRegex = new RegExp(`[\\d${decimalSeparator}]`);

    // Check if the input already contains a decimal separator
    const hasDecimalSeparator = amount.includes(decimalSeparator);

    if (event.ctrlKey || event.metaKey) {
      return;
    }

    if (
      (!allowedCharactersRegex.test(event.key) || (event.key === decimalSeparator && hasDecimalSeparator)) && // Allow digits and dot
      event.key !== "Backspace" &&
      event.key !== "Delete" &&
      event.key !== "ArrowLeft" &&
      event.key !== "ArrowRight" &&
      event.key !== "Tab" &&
      event.key !== "Enter" &&
      event.key !== "-"
    ) {
      event.preventDefault();
    }

    if (event.key === "-" && amount.includes("-")) {
      event.preventDefault();
    }

    if (event.key === "Delete" || event.key === "Backspace") {
      const inputElement = event.target as HTMLInputElement;
      const start = inputElement.selectionStart || 0;
      const end = inputElement.selectionEnd || 0;

      if (start !== end) {
        const newValue = amount.slice(0, start) + amount.slice(end);
        handleChangeAmount(newValue);
      } else if (event.key === "Backspace" && amount[start - 1] === thousandSeparator) {
        const newValue = amount.slice(0, start - 2) + amount.slice(start);

        if (inputRef.current) {
          const { selectionStart, selectionEnd } = inputRef.current;
          const newCursorPosition = selectionStart || 1;

          inputRef.current.setSelectionRange(newCursorPosition - 1, newCursorPosition - 1);
        }

        handleChangeAmount(newValue);
      }
    } else if (
      inputRef.current &&
      inputRef.current.selectionStart !== inputRef.current.selectionEnd &&
      (allowedCharactersRegex.test(event.key) || (event.key === decimalSeparator && hasDecimalSeparator))
    ) {
      // Prevent the default keydown behavior to prevent the character from being added twice
      event.preventDefault();

      // Replace the selected text with the typed key
      const selectedStart = inputRef.current.selectionStart || 0;
      const selectedEnd = inputRef.current.selectionEnd || 0;

      const newValue = amount.slice(0, selectedStart) + event.key + amount.slice(selectedEnd);

      if (inputRef.current) {
        const { selectionStart, selectionEnd } = inputRef.current;
        const newCursorPosition = selectionStart || 1;

        inputRef.current.setSelectionRange(newCursorPosition + 1, newCursorPosition + 1);
      }

      handleChangeAmount(newValue);
    }

    // Handle arrow keys and adjust cursor position
    if (inputRef.current) {
      const { selectionStart, selectionEnd } = inputRef.current;
      let newCursorPosition = selectionStart || 1;

      let nextChar = "";

      if (event.key === "ArrowLeft") {
        nextChar = amount[newCursorPosition - 1];

        newCursorPosition = newCursorPosition - (nextChar === thousandSeparator ? 1 : 0);

        inputRef.current.setSelectionRange(newCursorPosition, newCursorPosition);
      } else if (event.key === "ArrowRight") {
        newCursorPosition = selectionStart || 0;

        nextChar = amount[newCursorPosition + 1];

        newCursorPosition = newCursorPosition + (nextChar === thousandSeparator ? 1 : 0);

        inputRef.current.setSelectionRange(newCursorPosition, newCursorPosition);
      }
    }
  };
  // #endregion

  useEffect(() => {
    if (!formattedValue) {
      if (amountValue !== null && !firstRender && !formatAmountValue) {
        setAmount(amountValue.toLocaleCustom("allDigits"));
      } else if (amountValue !== null && !firstRender && formatAmountValue) {
        setFormatAmountValue(false);
        setAmount(amountValue.toLocaleCustom());
      }
    }
  }, [amountValue]);

  useEffect(() => {
    if (!firstRender && !!formattedValue) {
      setAmount(formattedValue);

      setFormattedValue("");
    }
  }, [amountValue, formattedValue]);

  // #region use layout effect
  useLayoutEffect(() => {
    const isFieldActive = document.activeElement === inputRef.current;

    if (isFieldActive) {
      if (inputRef.current) {
        const integerPartOfOldAmount = oldAmount.split(decimalSeparator)[0];
        const integerPartOfNewAmount = amount.split(decimalSeparator)[0];

        if (
          integerPartOfNewAmount.length > integerPartOfOldAmount.length &&
          integerPartOfNewAmount.length - integerPartOfOldAmount.length === 2
        ) {
          inputRef.current.selectionStart = cursorNewPosition + 1;
          inputRef.current.selectionEnd = cursorNewPosition + 1;
        } else if (
          integerPartOfNewAmount.length < integerPartOfOldAmount.length &&
          integerPartOfOldAmount.length - integerPartOfNewAmount.length === 2
        ) {
          inputRef.current.selectionStart = cursorNewPosition - 1;
          inputRef.current.selectionEnd = cursorNewPosition - 1;
        } else {
          inputRef.current.selectionStart = cursorNewPosition;
          inputRef.current.selectionEnd = cursorNewPosition;
        }
      }
    }
  }, [amount]);
  // #endregion

  useEffect(() => {
    if (firstRender) {
      setFirstRender(false);
      setAmount(amountValue.toLocaleCustom());
    }
  }, []);

  return (
    <TextField
      placeholder={placeHolder}
      required={required}
      variant="outlined"
      key={field}
      label={labelKey}
      type={"text"}
      value={amount}
      onChange={({ target: { value } }) => {
        handleChangeAmount(value);
      }}
      disabled={disabled}
      inputRef={inputRef}
      onBlur={({ target: { value } }) => {
        if (canBeConvertedToNumber(value, true)) {
          setFormatAmountValue(true);
          handleChange(field, value.toNumberFromLocaleCustom(2));

          const integerPartOfValue = value.split(decimalSeparator)[0];
          !!integerPartOfValue &&
            !(integerPartOfValue.length > 1 && Number(integerPartOfValue) === 0) &&
            setAmount(value.toNumberFromLocaleCustom(2).toLocaleCustom(2));
        }
      }}
      onKeyDown={handleKeyDown}
      sx={{
        ...(sxProps ? sxProps : {}),
      }}
      InputProps={{
        endAdornment: <>{!!endAdornment && endAdornment}</>,
      }}
      error={error}
      helperText={helperText}
    />
  );
};
